package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.model.AuthUser
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.FirebaseFirestoreException
import dev.gitlive.firebase.functions.FirebaseFunctionsException
import dev.gitlive.firebase.functions.FunctionsExceptionCode
import dev.gitlive.firebase.functions.code
import dev.gitlive.firebase.firestore.FirestoreExceptionCode
import dev.gitlive.firebase.firestore.code
import diana.soleil.design.iraniancanadienbazzar.core.common.CrashReporting
import kotlinx.coroutines.TimeoutCancellationException

/**
 * Collection names, write caps and small helpers shared by every `Firestore*Repository`.
 *
 * These were `private` top-level declarations in the old single-file FirestoreRepositories.kt;
 * they are `internal` now so the per-domain repository files can share them without widening the
 * module's public API.
 */

/**
 * Cap on awaiting server ack for user-facing one-shot writes. With offline persistence the SDK
 * queues the write and the suspend never resumes while offline — without a cap the submit spinner
 * hangs forever. On timeout the write is NOT lost (it syncs on reconnect); the UI just gets
 * [AppError.Network] instead of an infinite spinner.
 */
internal const val WRITE_TIMEOUT_MS = 10_000L

/** Cap for admin rejection reasons (stored on the doc + folded into the outcome notification). */
internal const val REJECTION_REASON_MAX = 300

internal const val BUSINESSES = "businesses"

internal const val CLAIMS = "claims"

// Storage folder for claim ownership proofs — private per-claimant (see firebase/storage.rules).
internal const val CLAIM_PROOFS_STORAGE = "claim-proofs"

internal const val RECOMMENDATIONS = "recommendations"

internal const val CATEGORIES = "categories"

internal const val USERS = "users"

internal const val REPORTS = "reports"

internal const val DEALS = "deals"

internal const val MODERATION_ACTIONS = "moderationActions"

internal const val LISTING_REQUESTS = "listingRequests"

/** Owner requests to change an approved listing's name/category/address (admin-approved). */
internal const val LISTING_CHANGE_REQUESTS = "listingChangeRequests"

internal const val REVIEWS = "reviews"

internal const val FOLLOWS = "follows"

internal const val CONVERSATIONS = "conversations"

internal const val CHANNELS = "channels"

internal const val CHANNEL_POSTS = "posts"

internal const val PUBLIC_PROFILES = "publicProfiles"

internal const val POSTS = "posts"

internal const val COMMENTS = "comments"

internal const val LIKES = "likes"

internal const val SAVED_POSTS = "savedPosts"

internal const val BLOCKED_USERS = "blockedUsers"

internal const val MARKETPLACE = "marketplaceItems"

/** Storage folder for listing photos — matches firebase/storage.rules `/marketplace/{itemId}`. */
internal const val MARKETPLACE_STORAGE = "marketplace"

internal const val PRODUCTS = "products"

internal const val ORDERS = "orders"

internal const val TICKET_TYPES = "ticketTypes"

internal const val METRICS = "metrics"

internal const val METRICS_COUNTERS = "counters"

internal const val TICKETS = "tickets"

internal const val EVENTS = "events"

internal const val RSVPS = "rsvps"

internal const val MESSAGES = "messages"

internal const val NOTIFICATIONS = "notifications"

internal const val NEWS = "news"

internal const val DEVICES = "devices"

internal const val REVIEW_VISIBLE = "visible"

internal const val STATUS_APPROVED = "approved"

internal const val STATUS_PENDING = "pending"

internal const val REPORT_OPEN = "open"

internal const val ROLE_USER = "user"

/**
 * Display name stamped onto authored docs (posts/reviews/listings/comments). Email/password
 * accounts often carry an EMPTY (not null) displayName, so a plain elvis fallback silently wrote
 * "" — blank-check before falling back to the email prefix.
 */
internal val AuthUser.publicName: String
    get() = displayName?.takeIf { it.isNotBlank() } ?: email?.substringBefore('@') ?: ""

internal inline fun <reified T> DocumentSnapshot.decode(): T = data<T>()

/**
 * Maps GitLive/Firestore failures onto the app's [AppError] taxonomy so ViewModels can branch on
 * offline vs denied vs missing instead of rendering every failure as a generic error. UNAVAILABLE
 * covers airplane mode/unreachable backend; PERMISSION_DENIED surfaces rules denials (e.g. signed
 * out mid-session) as [AppError.Unauthorized], the same type the client-side pre-checks produce.
 */
internal fun Throwable.toAppError(): AppError = when (this) {
    is FirebaseFirestoreException -> when (code) {
        FirestoreExceptionCode.UNAVAILABLE,
        FirestoreExceptionCode.DEADLINE_EXCEEDED,
        -> AppError.Network(this)
        FirestoreExceptionCode.PERMISSION_DENIED,
        FirestoreExceptionCode.UNAUTHENTICATED,
        -> AppError.Unauthorized(this)
        FirestoreExceptionCode.NOT_FOUND -> AppError.NotFound(this)
        FirestoreExceptionCode.ALREADY_EXISTS -> AppError.Conflict(this)
        else -> AppError.Unknown(this)
    }
    // Callables (GitLive `FirebaseFunctionsException`) carry the gRPC code the Function threw —
    // `not-found` from sendCampaign's target check, `permission-denied` from its role check…
    is FirebaseFunctionsException -> when (code) {
        FunctionsExceptionCode.UNAVAILABLE,
        FunctionsExceptionCode.DEADLINE_EXCEEDED,
        -> AppError.Network(this)
        FunctionsExceptionCode.PERMISSION_DENIED,
        FunctionsExceptionCode.UNAUTHENTICATED,
        -> AppError.Unauthorized(this)
        FunctionsExceptionCode.NOT_FOUND -> AppError.NotFound(this)
        FunctionsExceptionCode.ALREADY_EXISTS -> AppError.Conflict(this)
        else -> AppError.Unknown(this)
    }
    is TimeoutCancellationException -> AppError.Network(this)
    else -> AppError.Unknown(this)
}.also { mapped ->
    // Expected classes (offline, rules denial, missing doc, conflict, write timeout) are normal
    // product states; everything that maps to Unknown is a caught-but-unexpected failure that was
    // previously invisible — record it so launch-day triage has data.
    if (mapped is AppError.Unknown) CrashReporting.reporter.recordException(this)
}
